import React from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { Avatar, AVATAR_SIZE } from '@depop/web-ui-kit/Avatar';
import { useTranslations } from 'next-intl';

import { PurchaseBreakdown } from './PurchaseBreakdown';
import styles from './styles.module.css';

import { DepopProtectionNotice } from '@/app/[locale]/pay/components/DepopProtectionNotice';
import { Feedback } from '@/components/Feedback';
import {
  type CheckoutProduct,
  type OrderSummaryData,
} from '@/modules/checkout/types';
import { useIsCombinedFlowVariant } from '@/modules/experiments/useIsCombinedFlowVariant';
import { getNearestSizePictureFormat } from '@/modules/pictures/helpers';

type Props = {
  products: CheckoutProduct[];
  canShipItem?: boolean;
  checkoutSummary?: OrderSummaryData;
  hideTitle?: boolean;
  isInternationalShippingUnavailable?: boolean;
  withSoldOutHandling?: boolean;
  withRemoveButton?: boolean;
};

export function CheckoutOrderSummaryPanelV2({
  products,
  checkoutSummary,
  canShipItem,
  hideTitle = false,
  isInternationalShippingUnavailable = false,
  withSoldOutHandling = false,
  withRemoveButton = false,
}: Props) {
  const t = useTranslations('checkout');
  const isCombinedSignupFlow = useIsCombinedFlowVariant();

  const [{ seller }] = products;
  const feedbackTotal = seller.reviewsTotal || 0;
  const hasFeedback = feedbackTotal > 0;

  return (
    <div className={styles.orderSummaryContainer}>
      {!hideTitle && (
        <Heading variant="md" className={styles.title}>
          {t('OrderSummaryTitle')}
        </Heading>
      )}

      <div className={styles.sellerContainer}>
        <Avatar
          initials={seller.initials}
          size={AVATAR_SIZE.EXTRA_SMALL}
          verified={seller.verified}
          src={getNearestSizePictureFormat(seller.picture, 32)?.url}
        />
        <div className={styles.sellerInfo}>
          <Body variant="sm-regular" className={styles.sellerUsername}>
            {seller.username}
          </Body>
          {hasFeedback && (
            <Feedback
              className={styles.feedback}
              sellerId={seller.id}
              sellerUsername={seller.username}
              rating={seller.reviewsRating || 0}
              count={feedbackTotal}
            />
          )}
        </div>
      </div>

      {checkoutSummary && (
        <PurchaseBreakdown
          checkoutSummary={checkoutSummary}
          products={products}
          canShipItem={Boolean(canShipItem)}
          isInternationalShippingUnavailable={
            isInternationalShippingUnavailable
          }
          withSoldOutHandling={withSoldOutHandling}
          withRemoveButton={withRemoveButton}
        />
      )}
      {isCombinedSignupFlow && (
        <DepopProtectionNotice
          productId={products[0].id}
          sellerId={products[0].seller.id}
        />
      )}
    </div>
  );
}
